// src/components/AssetPPMManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const ENGINE_PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

export default function AssetPPMManager() {
  // ───────────────────────── All assets
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetSearch, setAssetSearch] = useState("");

  // ───────────────────────── Selected asset / mode
  const [selectedAssetId, setSelectedAssetId] = useState(null); // null = nothing, "NEW" = new machine
  const [assetForm, setAssetForm] = useState(makeEmptyAssetForm());
  const [plans, setPlans] = useState([]);
  const [originalPlanIds, setOriginalPlanIds] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // ───────────────────────── Load assets list once
  useEffect(() => {
    (async () => {
      try {
        setAssetsLoading(true);
        const snap = await getDocs(collection(db, "assets"));
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setAssets(list);
      } catch (e) {
        console.error("Failed to load assets", e);
      } finally {
        setAssetsLoading(false);
      }
    })();
  }, []);

  // ───────────────────────── Filtered list for sidebar
  const filteredAssets = useMemo(() => {
    const term = assetSearch.toLowerCase();
    if (!term) return assets;
    return assets.filter((a) => {
      const hay = `${a.name || ""} ${a.code || ""} ${a.location || ""} ${
        a.department || ""
      }`.toLowerCase();
      return hay.includes(term);
    });
  }, [assets, assetSearch]);

  // ───────────────────────── Handlers

  const startNewAsset = () => {
    setSelectedAssetId("NEW");
    setAssetForm(makeEmptyAssetForm());
    setPlans([makeEmptyPlan()]);
    setOriginalPlanIds([]);
    setMsg("");
  };

  const onSelectAsset = async (id) => {
    if (!id) return;
    setSelectedAssetId(id);
    setDetailLoading(true);
    setMsg("");

    try {
      // 1) asset
      const assetRef = doc(db, "assets", id);
      const assetSnap = await getDoc(assetRef);
      if (!assetSnap.exists()) {
        console.warn("Asset not found", id);
        return;
      }
      const a = assetSnap.data() || {};
      setAssetForm({
        name: a.name || "",
        code: a.code || "",
        location: a.location || "",
        department: a.department || "",
        serial: a.serial || "",
        vendor: a.vendor || "",
        installedAt: a.installedAt?.toDate
          ? toDateInputValue(a.installedAt.toDate())
          : "",
        active: a.active !== false,
      });

      // 2) plans
      const qPlans = query(
        collection(db, "ppm_plans"),
        where("assetId", "==", id),
        orderBy("title", "asc")
      );
      const snapPlans = await getDocs(qPlans);
      const loadedPlans = snapPlans.docs.map((d) =>
        planDocToUI(d.id, d.data() || {})
      );
      setPlans(loadedPlans.length ? loadedPlans : [makeEmptyPlan()]);
      setOriginalPlanIds(loadedPlans.map((p) => p.id).filter(Boolean));
    } catch (e) {
      console.error("Failed to load asset details", e);
      setMsg("❌ Failed to load machine details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const onAssetField = (e) => {
    const { name, value, type, checked } = e.target;
    setAssetForm((s) => ({
      ...s,
      [name]: type === "checkbox" ? !!checked : value,
    }));
  };

  const addPlan = () => {
    setPlans((s) => [...s, makeEmptyPlan()]);
  };

  const removePlan = (idx) => {
    setPlans((s) => s.filter((_, i) => i !== idx));
  };

  const duplicatePlan = (idx) => {
    setPlans((s) => {
      const copy = deepClone(s[idx]);
      copy.id = undefined; // new plan
      copy.title = copy.title ? `${copy.title} (copy)` : "";
      const next = [...s.slice(0, idx + 1), copy, ...s.slice(idx + 1)];
      return next;
    });
  };

  const onPlanField = (idx, path, value) => {
    setPlans((s) => {
      const next = deepClone(s);
      setByPath(next[idx], path, value);
      return next;
    });
  };

  const toggleWeeklyDay = (idx, day) => {
    setPlans((s) => {
      const next = deepClone(s);
      const set = new Set(next[idx].cadence.byWeekday || []);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      next[idx].cadence.byWeekday = Array.from(set);
      return next;
    });
  };

  // ───────────────────────── Validation

  const assetValid = useMemo(() => {
    return assetForm.name.trim() && assetForm.code.trim();
  }, [assetForm]);

  const plansValid = useMemo(() => {
    return plans.length > 0 && plans.every(validatePlan);
  }, [plans]);

  const canSave = assetValid && plansValid && !saving;

  // ───────────────────────── Save (create or update)

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setMsg("");

    try {
      const installedJs = assetForm.installedAt
        ? new Date(assetForm.installedAt + "T00:00:00")
        : null;

      const assetDoc = {
        name: assetForm.name.trim(),
        code: assetForm.code.trim(),
        location: assetForm.location.trim(),
        department: assetForm.department.trim(),
        serial: assetForm.serial.trim(),
        vendor: assetForm.vendor.trim(),
        active: !!assetForm.active,
        installedAt: installedJs ? Timestamp.fromDate(installedJs) : null,
        updatedAt: serverTimestamp(),
      };

      let assetId = selectedAssetId;

      if (selectedAssetId === "NEW" || !selectedAssetId) {
        // Create asset
        const ref = await addDoc(collection(db, "assets"), {
          ...assetDoc,
          createdAt: serverTimestamp(),
        });
        assetId = ref.id;
        setSelectedAssetId(assetId);
        // Also push into local list so sidebar updates
        setAssets((prev) =>
          [...prev, { id: assetId, ...assetDoc }].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          )
        );
      } else {
        // Update asset
        await updateDoc(doc(db, "assets", assetId), assetDoc);
        // Update local list
        setAssets((prev) =>
          prev
            .map((a) =>
              a.id === assetId ? { ...a, ...assetDoc } : a
            )
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "")
            )
        );
      }

      // Normalise plans -> Firestore writes
      const currentPlanIds = new Set(
        plans.map((p) => (p.id ? p.id : null)).filter(Boolean)
      );

      const deletes = originalPlanIds.filter(
        (id) => !currentPlanIds.has(id)
      );

      const planWrites = [];

      for (const p of plans) {
        const anchorJs = p.cadence.anchorDate
          ? new Date(p.cadence.anchorDate + "T00:00:00")
          : null;

        const planDoc = {
          assetId,
          assetName: assetDoc.name,
          title: p.title.trim(),
          description: p.description.trim(),
          priority: p.priority,
          requiresPhoto: !!p.requiresPhoto,
          checklist: normalizeChecklist(p.checklistRaw),
          timezone: "Europe/London",
          cadence: {
            freq: p.cadence.freq,
            interval: Number(p.cadence.interval || 1),
            byWeekday:
              p.cadence.freq === "WEEKLY"
                ? p.cadence.byWeekday || []
                : null,
            byMonthDay:
              p.cadence.freq === "MONTHLY" ||
              p.cadence.freq === "QUARTERLY" ||
              p.cadence.freq === "YEARLY"
                ? (p.cadence.byMonthDay
                    ? Number(p.cadence.byMonthDay)
                    : null)
                : null,
            anchorDate: anchorJs
              ? Timestamp.fromDate(anchorJs)
              : null,
          },
          nextDue: anchorJs ? Timestamp.fromDate(anchorJs) : null,
          active: true,
          updatedAt: serverTimestamp(),
        };

        if (p.id) {
          // Update existing plan
          planWrites.push(
            updateDoc(doc(db, "ppm_plans", p.id), planDoc)
          );
        } else {
          // New plan
          planWrites.push(
            addDoc(collection(db, "ppm_plans"), {
              ...planDoc,
              createdAt: serverTimestamp(),
            })
          );
        }
      }

      // Soft-deactivate removed plans
      for (const id of deletes) {
        planWrites.push(
          updateDoc(doc(db, "ppm_plans", id), {
            active: false,
            updatedAt: serverTimestamp(),
          })
        );
      }

      await Promise.all(planWrites);

      setOriginalPlanIds(
        plans.map((p) => p.id).filter(Boolean) // new ones will only have id after reload, but fine
      );

      setMsg(
        "✅ Machine and PPM tasks saved. (Scheduler / ppmExpandJobsNow will handle jobs.)"
      );
    } catch (e) {
      console.error(e);
      setMsg("❌ " + (e.message || "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  // ───────────────────────── Render

  const selectedAssetName =
    selectedAssetId === "NEW"
      ? "New Machine"
      : assets.find((a) => a.id === selectedAssetId)?.name || "";

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar: Machines */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Machines
              </h2>
              <button
                className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={startNewAsset}
              >
                + New
              </button>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Search
              </label>
              <input
                className="w-full p-2 border rounded-md text-sm"
                placeholder="Name, code, location…"
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
              />
            </div>

            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
              {assetsLoading && (
                <Muted className="py-2">Loading machines…</Muted>
              )}
              {!assetsLoading && !filteredAssets.length && (
                <Muted className="py-2">
                  No machines yet. Click “New”.
                </Muted>
              )}
              {filteredAssets.map((a) => {
                const active = selectedAssetId === a.id;
                return (
                  <button
                    key={a.id}
                    className={`w-full text-left px-2 py-2 text-sm hover:bg-gray-50 ${
                      active ? "bg-indigo-50" : ""
                    }`}
                    onClick={() => onSelectAsset(a.id)}
                  >
                    <div className="font-medium text-gray-900 truncate">
                      {a.name || "Untitled"}
                    </div>
                    <div className="text-xs text-gray-500 flex justify-between">
                      <span>{a.code}</span>
                      <span>{a.location}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Main: Asset + Plans */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedAssetName || "Select a machine or create new"}
                </h2>
                {selectedAssetId && (
                  <p className="text-xs text-gray-500">
                    Editing machine details and PPM tasks.
                  </p>
                )}
              </div>
              {assetForm.active && (
                <Badge tone="good">Active</Badge>
              )}
              {!assetForm.active && (
                <Badge tone="neutral">Inactive</Badge>
              )}
            </div>

            {!selectedAssetId && (
              <Muted>
                Choose a machine on the left or click “New” to add one.
              </Muted>
            )}

            {selectedAssetId && (
              <>
                {/* Asset form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Text
                    label="Name *"
                    name="name"
                    value={assetForm.name}
                    onChange={onAssetField}
                  />
                  <Text
                    label="Code/Tag *"
                    name="code"
                    value={assetForm.code}
                    onChange={onAssetField}
                  />
                  <Text
                    label="Location"
                    name="location"
                    value={assetForm.location}
                    onChange={onAssetField}
                  />
                  <Text
                    label="Department"
                    name="department"
                    value={assetForm.department}
                    onChange={onAssetField}
                  />
                  <Text
                    label="Serial"
                    name="serial"
                    value={assetForm.serial}
                    onChange={onAssetField}
                  />
                  <Text
                    label="Vendor"
                    name="vendor"
                    value={assetForm.vendor}
                    onChange={onAssetField}
                  />
                  <DateInput
                    label="Installed At"
                    name="installedAt"
                    value={assetForm.installedAt}
                    onChange={onAssetField}
                  />
                  <Checkbox
                    label="Active"
                    name="active"
                    checked={assetForm.active}
                    onChange={onAssetField}
                  />
                </div>
                <Muted className="mt-3 text-xs">
                  Fields marked * are required.
                </Muted>
              </>
            )}
          </Card>

          {/* Plans */}
          {selectedAssetId && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  PPM Tasks for this Machine
                </h3>
                <button
                  className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={addPlan}
                >
                  + Add Task
                </button>
              </div>

              {detailLoading && (
                <Muted>Loading tasks for this machine…</Muted>
              )}

              {!detailLoading && plans.length === 0 && (
                <Muted>No tasks yet. Click “Add Task”.</Muted>
              )}

              <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                {plans.map((p, idx) => {
                  const valid = validatePlan(p);
                  return (
                    <div
                      key={p.id || idx}
                      className="rounded-lg border border-gray-200 bg-white shadow-sm"
                    >
                      {/* Summary header */}
                      <div className="p-3 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {p.title.trim() ||
                              `Untitled Task #${idx + 1}`}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Chip tone={toneForPriority(p.priority)}>
                              {p.priority}
                            </Chip>
                            <Muted className="text-xs">
                              {labelForCadence(p.cadence)}
                            </Muted>
                            {!valid && (
                              <span className="text-[11px] text-red-600">
                                Incomplete
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            className="text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
                            onClick={() => duplicatePlan(idx)}
                          >
                            Duplicate
                          </button>
                          <button
                            className="text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
                            onClick={() => removePlan(idx)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="px-4 pb-4">
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Text
                            label="Task Title *"
                            value={p.title}
                            onChange={(e) =>
                              onPlanField(
                                idx,
                                "title",
                                e.target.value
                              )
                            }
                          />
                          <Select
                            label="Priority"
                            value={p.priority}
                            options={ENGINE_PRIORITIES}
                            onChange={(e) =>
                              onPlanField(
                                idx,
                                "priority",
                                e.target.value
                              )
                            }
                          />
                          <Textarea
                            label="Description"
                            value={p.description}
                            onChange={(e) =>
                              onPlanField(
                                idx,
                                "description",
                                e.target.value
                              )
                            }
                          />
                          <Textarea
                            label="Checklist (one per line)"
                            value={p.checklistRaw}
                            onChange={(e) =>
                              onPlanField(
                                idx,
                                "checklistRaw",
                                e.target.value
                              )
                            }
                          />
                          <Checkbox
                            label="Requires Photo on Completion"
                            checked={p.requiresPhoto}
                            onChange={(e) =>
                              onPlanField(
                                idx,
                                "requiresPhoto",
                                e.target.checked
                              )
                            }
                          />
                        </div>

                        {/* Cadence */}
                        <div className="mt-4 rounded-md border border-gray-200 p-3 bg-gray-50">
                          <div className="text-sm font-medium text-gray-900 mb-2">
                            Cadence
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Select
                              label="Frequency *"
                              value={p.cadence.freq}
                              options={[
                                "DAILY",
                                "WEEKLY",
                                "MONTHLY",
                                "QUARTERLY",
                                "YEARLY",
                              ]}
                              onChange={(e) =>
                                onPlanField(
                                  idx,
                                  "cadence.freq",
                                  e.target.value
                                )
                              }
                            />
                            <NumberInput
                              label="Every N"
                              min={1}
                              value={p.cadence.interval}
                              onChange={(e) =>
                                onPlanField(
                                  idx,
                                  "cadence.interval",
                                  e.target.value
                                )
                              }
                            />
                            <DateInput
                              label="First Due (Anchor) *"
                              name="anchorDate"
                              value={p.cadence.anchorDate}
                              onChange={(e) =>
                                onPlanField(
                                  idx,
                                  "cadence.anchorDate",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          {p.cadence.freq === "WEEKLY" && (
                            <div className="mt-3">
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Weekdays
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {WEEKDAYS.map((d) => {
                                  const active =
                                    p.cadence.byWeekday.includes(d);
                                  return (
                                    <button
                                      key={d}
                                      type="button"
                                      onClick={() =>
                                        toggleWeeklyDay(idx, d)
                                      }
                                      className={`text-xs px-2 py-1 rounded-md border ${
                                        active
                                          ? "bg-indigo-600 text-white border-indigo-600"
                                          : "bg-white hover:bg-gray-50"
                                      }`}
                                    >
                                      {d}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {(p.cadence.freq === "MONTHLY" ||
                            p.cadence.freq === "QUARTERLY" ||
                            p.cadence.freq === "YEARLY") && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                              <NumberInput
                                label="Day of Month"
                                min={1}
                                max={31}
                                value={p.cadence.byMonthDay || ""}
                                onChange={(e) =>
                                  onPlanField(
                                    idx,
                                    "cadence.byMonthDay",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>

                        {!valid && (
                          <div className="mt-2 text-xs text-red-600">
                            Please complete title, frequency, anchor
                            date, and cadence specifics.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Save bar */}
          {selectedAssetId && (
            <div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className="text-sm font-medium text-gray-900">
                    {assetValid ? "Asset valid" : "Asset incomplete"} ·{" "}
                    {plansValid ? "Plans valid" : "Plans incomplete"}
                  </div>
                  {msg && (
                    <div className="text-sm mt-1 whitespace-pre-wrap">
                      {msg}
                    </div>
                  )}
                </div>
                <div>
                  <button
                    onClick={onSave}
                    disabled={!canSave}
                    className={`px-4 py-2 rounded-md text-white text-sm ${
                      canSave
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {saving ? "Saving…" : "Save Machine + Tasks"}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Jobs will still be generated by your scheduled Cloud
                Function (e.g. <code>ppmExpandJobsNow</code>).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── UI atoms ───────────────────────── */

function Card({ children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      {children}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const cls = {
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    neutral: "bg-gray-50 text-gray-700 border-gray-200",
  }[tone];
  return (
    <span
      className={`text-xs border rounded-full px-2 py-0.5 ${cls}`}
    >
      {children}
    </span>
  );
}

function Chip({ children, tone = "neutral" }) {
  const cls = {
    danger: "bg-red-50 text-red-700 border-red-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    neutral: "bg-gray-50 text-gray-700 border-gray-200",
  }[tone];
  return (
    <span
      className={`text-[11px] border rounded-full px-2 py-0.5 ${cls}`}
    >
      {children}
    </span>
  );
}

function Muted({ children, className = "" }) {
  return (
    <p className={`text-sm text-gray-500 ${className}`}>{children}</p>
  );
}

function Text({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
      </label>
      <input
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
        name={name}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function Textarea({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
      </label>
      <textarea
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
        rows={3}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
      </label>
      <select
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
        value={value}
        onChange={onChange}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberInput({ label, value, onChange, min = 1, max = 999 }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function DateInput({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
      </label>
      <input
        type="date"
        name={name}
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function Checkbox({ label, name, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 mt-6 text-sm">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4"
      />
      <span className="text-gray-800">{label}</span>
    </label>
  );
}

/* ───────────────────────── helpers ───────────────────────── */

function makeEmptyAssetForm() {
  return {
    name: "",
    code: "",
    location: "",
    department: "",
    serial: "",
    vendor: "",
    installedAt: "",
    active: true,
  };
}

function makeEmptyPlan() {
  return {
    id: undefined,
    title: "",
    description: "",
    priority: "Normal",
    requiresPhoto: false,
    checklistRaw: "",
    cadence: {
      freq: "WEEKLY",
      interval: 1,
      byWeekday: ["FR"],
      byMonthDay: "",
      anchorDate: "",
    },
  };
}

function validatePlan(p) {
  if (!p.title.trim()) return false;
  if (!p.cadence?.freq) return false;
  if (!p.cadence?.anchorDate) return false;

  if (
    p.cadence.freq === "WEEKLY" &&
    (!p.cadence.byWeekday || p.cadence.byWeekday.length === 0)
  ) {
    return false;
  }
  if (
    (p.cadence.freq === "MONTHLY" ||
      p.cadence.freq === "QUARTERLY" ||
      p.cadence.freq === "YEARLY") &&
    !p.cadence.byMonthDay
  ) {
    return false;
  }
  return true;
}

function normalizeChecklist(raw) {
  return (raw || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function setByPath(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = cur[parts[i]] ?? {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

function planDocToUI(id, data) {
  const cadence = data.cadence || {};
  const anchorTs = cadence.anchorDate;
  return {
    id,
    title: data.title || "",
    description: data.description || "",
    priority: data.priority || "Normal",
    requiresPhoto: !!data.requiresPhoto,
    checklistRaw: Array.isArray(data.checklist)
      ? data.checklist.join("\n")
      : "",
    cadence: {
      freq: cadence.freq || "WEEKLY",
      interval: cadence.interval || 1,
      byWeekday: Array.isArray(cadence.byWeekday)
        ? cadence.byWeekday
        : [],
      byMonthDay: cadence.byMonthDay || "",
      anchorDate: anchorTs?.toDate
        ? toDateInputValue(anchorTs.toDate())
        : "",
    },
  };
}

function labelForCadence(c) {
  if (!c?.freq) return "";
  const n = c.interval || 1;
  const every = n === 1 ? "Every" : `Every ${n}`;
  switch (c.freq) {
    case "DAILY":
      return `${every} day · Anchor ${c.anchorDate || "—"}`;
    case "WEEKLY":
      return `${every} week · ${
        Array.isArray(c.byWeekday) ? c.byWeekday.join(",") : "—"
      } · Anchor ${c.anchorDate || "—"}`;
    case "MONTHLY":
      return `${every} month · Day ${
        c.byMonthDay || "—"
      } · Anchor ${c.anchorDate || "—"}`;
    case "QUARTERLY":
      return `${every} quarter · Day ${
        c.byMonthDay || "—"
      } · Anchor ${c.anchorDate || "—"}`;
    case "YEARLY":
      return `${every} year · Day ${
        c.byMonthDay || "—"
      } · Anchor ${c.anchorDate || "—"}`;
    default:
      return "";
  }
}

function toneForPriority(p) {
  return (
    {
      Urgent: "danger",
      High: "warn",
      Normal: "info",
      Low: "neutral",
    }[p] || "neutral"
  );
}

function toDateInputValue(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
